import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Title, Text, Card, LineChart, Badge } from '@tremor/react';
import { getRetencaoCurvaRetorno, extractErrorMessage } from '@/lib/api';

// ── Tipos (payload de GET /retencao/curva-retorno) ──────────────────────────

interface Ponto {
  periodo: number;
  num: number;
  denom: number;
  pct: number | null;
}

interface Segmento {
  cohort: number;
  serie: Ponto[];
}

interface CurvaData {
  disponivel: boolean;
  erro?: string;
  unidade: 'semana' | 'dia';
  modo: 'cumulativo' | 'periodico';
  denominador: 'maduro' | 'fixo';
  cohort: number;
  returned_total: number;
  returned_pct: number | null;
  max_period: number;
  plateau_from: number | null;
  serie: Ponto[];
  segmentos: { pago: Segmento; gratis: Segmento };
  caveat: string;
}

type Unidade = 'semana' | 'dia';
type Modo = 'cumulativo' | 'periodico';
type Denominador = 'maduro' | 'fixo';

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtPct(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

// Botão de toggle no mesmo estilo da QualidadeVAPage.
function Toggle<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { v: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={`rounded px-2.5 py-1 text-xs transition ${
            value === o.v
              ? 'bg-blue-600/30 text-blue-200'
              : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function RetencaoCurvaCard() {
  const [unidade, setUnidade] = useState<Unidade>('semana');
  const [modo, setModo] = useState<Modo>('cumulativo');
  const [denominador, setDenominador] = useState<Denominador>('maduro');
  const [segmentar, setSegmentar] = useState(false);

  const { data, isLoading, error } = useQuery<CurvaData>({
    queryKey: ['retencao-curva', unidade, modo, denominador],
    queryFn: async () =>
      (await getRetencaoCurvaRetorno({ unidade, modo, denominador })).data,
    refetchInterval: 5 * 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar curva de retorno')
    : data && !data.disponivel
      ? (data.erro ?? 'dados indisponíveis')
      : null;

  const label = unidade === 'semana' ? 'sem' : 'dia';

  const chartData = useMemo(() => {
    if (!data?.disponivel) return [];
    const geral = new Map(data.serie.map((p) => [p.periodo, p]));
    const pago = new Map(data.segmentos.pago.serie.map((p) => [p.periodo, p]));
    const gratis = new Map(data.segmentos.gratis.serie.map((p) => [p.periodo, p]));
    return data.serie.map((p) => {
      const row: Record<string, number | string | null> = {
        periodo: `${label} ${p.periodo}`,
        '% do cohort': p.pct !== null ? +(p.pct * 100).toFixed(1) : null,
        n: p.denom,
      };
      if (segmentar) {
        const pg = pago.get(p.periodo);
        const gr = gratis.get(p.periodo);
        row['Pago'] = pg?.pct != null ? +(pg.pct * 100).toFixed(1) : null;
        row['Grátis'] = gr?.pct != null ? +(gr.pct * 100).toFixed(1) : null;
      }
      return row;
    });
  }, [data, segmentar, label]);

  const categories = segmentar ? ['Pago', 'Grátis'] : ['% do cohort'];
  const colors = segmentar ? ['emerald', 'slate'] : ['blue'];

  return (
    <section className="space-y-2">
      <Text className="text-xs uppercase tracking-wide text-slate-400">
        Curva de retorno (voltar a gerar cálculos)
      </Text>

      {/* Banner de caveat — bem visível */}
      <Card className="border border-amber-700/40 bg-amber-900/10 py-2">
        <Text className="text-xs text-amber-200/90">
          ⚠️ Tracking de <b>download</b> começou em 16/06 → cohorts jovens,
          profundidade ~3 semanas; cauda com <b>n pequeno é ruído</b>. 100% = o
          cohort (quem baixou); voltar a subir é comportamento minoritário
          (~18%), então o período 0 <b>não</b> é 100%.
        </Text>
      </Card>

      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <Title>Quem obteve um cálculo volta a gerar outros?</Title>
            <Text className="text-xs text-slate-400">
              Cohort = orgs que baixaram (t0 = 1º download). Retorno = subir um{' '}
              <b>novo</b> caso depois do t0. Eixo Y = % do cohort.
            </Text>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Toggle<Unidade>
              value={unidade}
              onChange={setUnidade}
              options={[
                { v: 'semana', label: 'semanas' },
                { v: 'dia', label: 'dias' },
              ]}
            />
            <Toggle<Modo>
              value={modo}
              onChange={setModo}
              options={[
                { v: 'cumulativo', label: 'cumulativo' },
                { v: 'periodico', label: 'periódico' },
              ]}
            />
            <Toggle<Denominador>
              value={denominador}
              onChange={setDenominador}
              options={[
                { v: 'maduro', label: 'cohort maduro' },
                { v: 'fixo', label: 'cohort fixo' },
              ]}
            />
            <Toggle<'geral' | 'seg'>
              value={segmentar ? 'seg' : 'geral'}
              onChange={(v) => setSegmentar(v === 'seg')}
              options={[
                { v: 'geral', label: 'geral' },
                { v: 'seg', label: 'pago × grátis' },
              ]}
            />
          </div>
        </div>

        {/* Explicação dos toggles (tooltip inline) */}
        <div className="mb-3 grid gap-1 text-[11px] leading-relaxed text-slate-500 sm:grid-cols-2">
          <div>
            <b>Modo:</b> <i>cumulativo</i> = % que JÁ voltou até o período n
            (monotônico); <i>periódico</i> = % ativo NO período n.
          </div>
          <div>
            <b>Denominador:</b> <i>cohort maduro</i> (default) = no período n só
            conta quem já teve n períodos de chance de voltar (não afunda a cauda
            com late-joiners); <i>cohort fixo</i> = todos os que baixaram.
          </div>
        </div>

        {isLoading && <Text className="mt-3">carregando…</Text>}
        {!isLoading && errMsg && (
          <div className="text-sm text-red-400" role="alert">
            {errMsg}
          </div>
        )}

        {!isLoading && !errMsg && data?.disponivel && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <Badge color="blue" size="sm">
                cohort {fmtInt(data.cohort)}
              </Badge>
              <Badge color="emerald" size="sm">
                voltaram {fmtInt(data.returned_total)} ({fmtPct(data.returned_pct)})
              </Badge>
              {data.plateau_from !== null && (
                <Badge color="amber" size="sm">
                  platô a partir de {label} {data.plateau_from}
                </Badge>
              )}
            </div>

            <LineChart
              className="mt-1 h-72"
              data={chartData}
              index="periodo"
              categories={categories}
              colors={colors}
              valueFormatter={(v) => `${v}%`}
              showAnimation
              connectNulls
              minValue={0}
            />

            {/* n por ponto — evita ler ruído na cauda (mesma lição do denom de VA) */}
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-slate-400">
                    <th className="py-1 pr-3 font-medium">período</th>
                    {data.serie.map((p) => (
                      <th key={p.periodo} className="px-2 py-1 text-right font-medium">
                        {label} {p.periodo}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-800/50 text-slate-300">
                    <td className="py-1 pr-3">% do cohort</td>
                    {data.serie.map((p) => (
                      <td key={p.periodo} className="px-2 py-1 text-right">
                        {fmtPct(p.pct)}
                      </td>
                    ))}
                  </tr>
                  <tr className="text-slate-500">
                    <td className="py-1 pr-3">n (cohort {denominador})</td>
                    {data.serie.map((p) => (
                      <td key={p.periodo} className="px-2 py-1 text-right">
                        {fmtInt(p.denom)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </section>
  );
}
